/** Справочная информация для всего интерфейса: колонки, категории, статусы, уровни, сотрудники. */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, type Bootstrap } from '@/api';
import { realtime } from '@/api/realtime';
import { useAuth } from './auth';

export const useMeta = defineStore('meta', () => {
  const data = ref<Bootstrap | null>(null);
  const loading = ref(false);
  const wsConnected = ref(false);
  let wired = false;

  async function load() {
    loading.value = true;
    try {
      data.value = await api.bootstrap();
    } finally {
      loading.value = false;
    }
  }

  let reloadTimer: number | null = null;
  function wire() {
    if (wired) return;
    wired = true;
    realtime.on((e) => {
      if (e.type === 'config') {
        if (reloadTimer) window.clearTimeout(reloadTimer);
        reloadTimer = window.setTimeout(() => void load(), 300);
      }
    });
    realtime.onStatus((v) => (wsConnected.value = v));
    // статус движка — раз в 15 секунд
    window.setInterval(async () => {
      if (!data.value) return;
      try {
        data.value.engine = (await api.engineStatus()).status;
      } catch {
        /* офлайн — индикатор покажет */
      }
    }, 15_000);
  }

  const columns = computed(() => (data.value?.columns ?? []).filter((c) => c.enabled).sort((a, b) => a.sort - b.sort));
  const allColumns = computed(() => data.value?.columns ?? []);
  const columnGroups = computed(() => data.value?.columnGroups ?? []);
  /**
   * Колонки доски — список решает сервер (boardColumnKeys): администратор видит в настройках все колонки,
   * а на доске — общие, свои личные и расшаренные через группы.
   */
  const boardColumns = computed(() => {
    const keys = data.value?.boardColumnKeys;
    if (keys) {
      const set = new Set(keys);
      return columns.value.filter((c) => set.has(c.key));
    }
    // старый сервер без boardColumnKeys
    const me = useAuth().user;
    if (me?.role !== 'admin') return columns.value;
    return columns.value.filter((c) => c.ownerUserId == null || c.ownerUserId === me.id);
  });
  const categories = computed(() => data.value?.categories ?? []);
  const resolutions = computed(() => (data.value?.resolutions ?? []).filter((r) => r.enabled));
  const levels = computed(() => [...(data.value?.levels ?? [])].sort((a, b) => a.afterSec - b.afterSec));
  const departments = computed(() => data.value?.departments ?? []);
  /** Настройки нерабочего времени; missedColumnKey — колонка «Пропущенные», если выбрана и включена. */
  const offHours = computed(() => data.value?.offHours ?? { missedColumnKey: null, timerFromWorkStart: false });
  const missedColumnKey = computed(() => {
    const k = offHours.value.missedColumnKey;
    return k && columns.value.some((c) => c.key === k) ? k : null;
  });

  const column = (key: string | null | undefined) => data.value?.columns.find((c) => c.key === key);
  const category = (key: string | null | undefined) => data.value?.categories.find((c) => c.key === key);
  const resolution = (key: string | null | undefined) => data.value?.resolutions.find((r) => r.key === key);
  const department = (key: string | null | undefined) => data.value?.departments.find((d) => d.key === key);

  /** Уровень срочности по времени ожидания. */
  function levelFor(waitedSec: number) {
    let current: (typeof levels.value)[number] | null = null;
    for (const l of levels.value) if (waitedSec >= l.afterSec) current = l;
    return current;
  }

  return {
    data,
    loading,
    wsConnected,
    load,
    wire,
    columns,
    allColumns,
    columnGroups,
    boardColumns,
    categories,
    resolutions,
    levels,
    departments,
    offHours,
    missedColumnKey,
    column,
    category,
    resolution,
    department,
    levelFor,
  };
});
